import React, { ReactElement, ReactNode, memo } from "react";
import Image from "next/image";
import { TFeatureList } from "@page-builder/ts/generated";
import { replacePlaceholders } from "@lib/sharedUtils";
import styles from "./styles.module.scss";

export type ListItemArray = Extract<
    NonNullable<NonNullable<TFeatureList["props"]["items"]>[number]["list"]>,
    any[]
>;
export type ListItem = ListItemArray[number];
export type FeatureItem = NonNullable<
    NonNullable<TFeatureList["props"]["items"]>[number]
>;

export interface FeatureListItemProps {
    item: FeatureItem;
    index: number;
    order: string[];
    itemLayout: string;
    isHorizontalButtons: boolean;
    showCounter: boolean;
    basePlaceholders: {
        region: string;
        city?: string;
    };
    postPlaceholders: {
        region: string;
        city?: string;
        postUpdatedDate?: string;
        readingTime?: string;
    };
    renderListItems: (list: ListItem[]) => ReactNode;
    renderAction: (item: FeatureItem) => ReactNode;
}

function areOrdersEqual(prev: string[], next: string[]): boolean {
    if (prev === next) return true;
    if (prev.length !== next.length) return false;
    return prev.every((key, index) => key === next[index]);
}

function areFeatureListItemPropsEqual(
    prev: FeatureListItemProps,
    next: FeatureListItemProps,
): boolean {
    return (
        prev.item === next.item &&
        prev.index === next.index &&
        prev.itemLayout === next.itemLayout &&
        prev.isHorizontalButtons === next.isHorizontalButtons &&
        prev.showCounter === next.showCounter &&
        areOrdersEqual(prev.order, next.order) &&
        prev.basePlaceholders.region === next.basePlaceholders.region &&
        prev.basePlaceholders.city === next.basePlaceholders.city &&
        prev.postPlaceholders.postUpdatedDate ===
            next.postPlaceholders.postUpdatedDate &&
        prev.postPlaceholders.readingTime ===
            next.postPlaceholders.readingTime &&
        prev.renderListItems === next.renderListItems &&
        prev.renderAction === next.renderAction
    );
}

function FeatureListItem({
    item,
    index,
    order,
    itemLayout,
    isHorizontalButtons,
    showCounter,
    basePlaceholders,
    postPlaceholders,
    renderListItems,
    renderAction,
}: FeatureListItemProps): ReactElement {
    const contentByKey = order.reduce<Record<string, ReactNode>>((acc, key) => {
        if (key === "title") {
            acc[key] =
                item.href && item.href.trim() ? (
                    <a href={item.href} className={styles["item-href"]}>
                        <div
                            className={`font-medium text-lg lg:text-xl ${styles["item-title"]}`}
                            dangerouslySetInnerHTML={{
                                __html: replacePlaceholders(
                                    item.title ?? "",
                                    basePlaceholders,
                                ),
                            }}
                        />
                    </a>
                ) : (
                    <div
                        className={`font-medium text-lg lg:text-xl ${styles["item-title"]}`}
                        dangerouslySetInnerHTML={{
                            __html: replacePlaceholders(
                                item.title ?? "",
                                postPlaceholders,
                            ),
                        }}
                    />
                );
        } else if (key === "description") {
            acc[key] = item.showList ? (
                <ul>
                    {Array.isArray(item.list) && renderListItems(item.list)}
                </ul>
            ) : (
                <p className="font-light text-sm text-center">
                    {replacePlaceholders(
                        item.description ?? "",
                        basePlaceholders,
                    )}
                </p>
            );
        } else if (key === "list" && !item.showList) {
            acc[key] =
                Array.isArray(item.list) && item.list.length > 0 ? (
                    <ul>{renderListItems(item.list)}</ul>
                ) : null;
        } else if (key === "note") {
            acc[key] = item.note ? (
                <div
                    className={`font-light text-sm ${styles["item-note"]}`}
                    dangerouslySetInnerHTML={{
                        __html: item.note,
                    }}
                />
            ) : null;
        } else if (key === "image") {
            if (showCounter) {
                acc[key] = (
                    <div className={`flex ${styles["imageContainer"]}`}>
                        <div className={styles["counter"]}>{index + 1}</div>
                    </div>
                );
            } else if (item.image) {
                acc[key] = (
                    <div className={`flex ${styles["imageContainer"]}`}>
                        <div className={`relative w-full ${styles["image"]}`}>
                            <Image
                                src={item.image}
                                layout="fill"
                                alt={item.title}
                            />
                        </div>
                    </div>
                );
            } else {
                acc[key] = null;
            }
        } else if (key === "button") {
            acc[key] = renderAction(item);
        } else {
            acc[key] = null;
        }

        return acc;
    }, {});

    const orderedContent = order.map((key) => (
        <React.Fragment key={key}>{contentByKey[key] ?? null}</React.Fragment>
    ));

    const itemClasses = `${styles.item} ${
        itemLayout === "horizontal" ? styles.horizontal : "flex-col"
    }`;

    return (
        <div className={`flex ${itemClasses}`}>
            {itemLayout === "horizontal" ? (
                <>
                    <div className={styles.imageColumn}>
                        {contentByKey.image ?? null}
                    </div>
                    <div className={styles.contentColumn}>
                        {order
                            .filter((key) => {
                                if (key === "image") {
                                    return false;
                                }
                                if (key === "button" && isHorizontalButtons) {
                                    return false;
                                }
                                return true;
                            })
                            .map((key) => (
                                <React.Fragment key={key}>
                                    {contentByKey[key] ?? null}
                                </React.Fragment>
                            ))}
                    </div>
                    {isHorizontalButtons && (
                        <div className={styles.buttonColumn}>
                            {contentByKey.button ?? null}
                        </div>
                    )}
                </>
            ) : (
                orderedContent
            )}
        </div>
    );
}

export default memo(FeatureListItem, areFeatureListItemPropsEqual);
